import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, DownloadSimple, Check, X } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTenantPath } from '../lib/tenantPath';
import ShareMenu from '../components/ShareMenu';
import { fileUrl } from '../lib/fileUrl';
import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductCompare = () => {
  const navigate = useNavigate();
  const tp = useTenantPath();
  const [searchParams] = useSearchParams();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareToken, setShareToken] = useState(null);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) fetchComparison(ids.split(','));
    else setLoading(false);
  }, [searchParams]);

  const fetchComparison = async (productIds) => {
    try {
      const resp = await axios.post(`${API}/products/compare`, productIds);
      setComparison(resp.data);
    } catch (error) {
      notify(error.response?.data?.detail || 'Error comparing products');
      navigate(tp('/products'));
    } finally { setLoading(false); }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const ids = comparison.products.map(p => p.id);
      const resp = await axios.post(`${API}/products/compare-pdf`, ids);
      setShareToken(resp.data.share_token);
      window.open(`${BACKEND_URL}${resp.data.download_url}`, '_blank');
    } catch (error) {
      notify(error.response?.data?.detail || 'Error generating PDF');
    } finally { setPdfLoading(false); }
  };

  const buildSharePayload = async () => {
    let token = shareToken;
    if (!token) {
      const ids = comparison.products.map(p => p.id);
      const resp = await axios.post(`${API}/products/compare-pdf`, ids);
      token = resp.data.share_token;
      setShareToken(token);
    }
    const url = `${BACKEND_URL}/api/share/comparison/${token}`;
    const names = comparison.products.map(p => p.name).join(' vs ');
    return {
      subject: `Product Comparison — ${names}`,
      body: `Hello,\n\nHere is a side-by-side comparison of the following products (${comparison.category}):\n\n${comparison.products.map(p => `• ${p.name} (${p.product_code})`).join('\n')}\n\nView the comparison PDF:\n${url}\n\nBest regards,`,
    };
  };

  if (loading) return <div className="flex items-center justify-center h-full">Loading comparison...</div>;
  if (!comparison) return <div className="flex items-center justify-center h-full">No products selected</div>;

  const { products, spec_keys, category } = comparison;
  const numProducts = products.length;

  const hasFeature = (specValue, featureName) => {
    if (!specValue) return false;
    return specValue.toLowerCase().includes(featureName.toLowerCase());
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="compare-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(tp('/products'))} className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition" data-testid="compare-back-btn">
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Product Comparison</h1>
          <p className="text-[#6D8E87] text-sm">Category: {category} — {numProducts} products</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleDownloadPdf} disabled={pdfLoading}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00a69c] text-white rounded-lg font-medium hover:bg-[#00bfb3] transition disabled:opacity-50"
            data-testid="compare-pdf-btn">
            <DownloadSimple size={18} weight="bold" />
            {pdfLoading ? 'Generating...' : 'Download PDF'}
          </button>
          <ShareMenu
            label="Share"
            testid="compare-share"
            getPayload={buildSharePayload}
          />
        </div>
      </div>

      {/* Product Header Cards */}
      <div className={`grid gap-4 mb-6 ${numProducts === 2 ? 'grid-cols-2' : 'grid-cols-3'}`} data-testid="compare-header-cards">
        {products.map((p) => (
          <div key={p.id} className="medical-card p-4 text-center" data-testid={`compare-product-${p.id}`}>
            <div className="h-40 bg-[#F4F9F7] rounded-lg mb-3 flex items-center justify-center overflow-hidden">
              {p.image ? (
                <img src={fileUrl(p.image)} alt={p.name} className="h-full w-full object-contain" />
              ) : (
                <div className="text-[#D5E8E4] text-sm">No Image</div>
              )}
            </div>
            <h3 className="text-lg font-bold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>{p.name}</h3>
            <p className="text-xs text-[#6D8E87] mb-2">{p.product_code}</p>
            <div className="text-2xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>
              {p.price > 0 ? `₹${p.price.toLocaleString('en-IN')}` : <span className="text-sm text-[#6D8E87] font-normal">Contact for Price</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Spec-by-Spec Comparison Table */}
      <div className="medical-card overflow-x-auto" data-testid="compare-specs-table">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-[#00a69c]">
              <th className="text-left px-5 py-3 text-white font-semibold text-xs uppercase tracking-wide w-1/4">Specification</th>
              {products.map(p => (
                <th key={p.id} className="text-left px-5 py-3 text-white font-semibold text-xs">{p.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Basic Info */}
            {[
              { label: 'Product Code', getter: p => p.product_code },
              { label: 'HSN Code', getter: p => p.hsn_code },
              { label: 'GST Rate', getter: p => `${p.tax_bracket}%` },
              { label: 'Price', getter: p => p.price > 0 ? `₹${p.price.toLocaleString('en-IN')}` : 'Contact for Price' },
            ].map(({ label, getter }) => (
              <tr key={label} className="border-t border-[#D5E8E4] hover:bg-[#FAFFFE] transition">
                <td className="px-5 py-3 font-semibold text-[#3B5A54] bg-[#F4F9F7]">{label}</td>
                {products.map(p => (
                  <td key={p.id} className="px-5 py-3 text-[#052A23]">{getter(p)}</td>
                ))}
              </tr>
            ))}

            {/* Extracted Specs */}
            {spec_keys.filter(k => k !== 'Key Features').map((key) => {
              const values = products.map(p => p.specs[key] || '—');
              const allSame = values.every(v => v === values[0]);
              return (
                <tr key={key} className="border-t border-[#D5E8E4] hover:bg-[#FAFFFE] transition">
                  <td className="px-5 py-3 font-semibold text-[#3B5A54] bg-[#F4F9F7]">{key}</td>
                  {values.map((val, i) => (
                    <td key={i} className={`px-5 py-3 ${!allSame && val !== '—' ? 'text-[#00a69c] font-medium' : 'text-[#052A23]'}`}>
                      {val}
                    </td>
                  ))}
                </tr>
              );
            })}

            {/* Features Checklist */}
            {spec_keys.includes('Key Features') && (() => {
              const allFeatures = new Set();
              products.forEach(p => {
                (p.specs['Key Features'] || '').split(', ').filter(Boolean).forEach(f => allFeatures.add(f));
              });
              return Array.from(allFeatures).map(feature => (
                <tr key={feature} className="border-t border-[#D5E8E4] hover:bg-[#FAFFFE] transition">
                  <td className="px-5 py-3 font-semibold text-[#3B5A54] bg-[#F4F9F7]">{feature}</td>
                  {products.map(p => {
                    const has = hasFeature(p.specs['Key Features'], feature);
                    return (
                      <td key={p.id} className="px-5 py-3 text-center">
                        {has ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100"><Check size={14} className="text-green-600" weight="bold" /></span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-50"><X size={14} className="text-red-400" weight="bold" /></span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductCompare;
